import React from 'react';
import Slider from "react-slick";
import moment from "moment";
import { PropertyDetails } from '../../../core/api/models/project';
import { convertPascalCaseToWords, ProposalType, ProposedProperty } from '../../../core';
import BlankProPic from "../../../styles/images/no-user-sm.png";
import PhoneNumberCopy from '../../../components/phoneNumberCopy/PhoneNumberCopy';
import EmailLink from '../../../components/emailLink/EmailLink';
import ShortlistedPropertiesDropdownTable from './shortlited-properties-dropdown-table';
import { FurnishingDetailsViewOption } from './props';
import ArrowDownIcon from '../../../assets/images/arrow-down';

export interface ShortlistedPropertiesListViewProps {
    selectedExcludeFloorDetails: PropertyDetails[];
    selectedExcludePhotoGalleryDetails: PropertyDetails[];
    selectedExcludeFurnishingDetails: PropertyDetails[];
    onUpdateFloorplanFurnishing: ((propertyId: string, value: string) => void) | undefined;
    selectedProperties: PropertyDetails[];
    sortList: any[];
    setShowThirdColumn: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>> | undefined;
    groupByList: any;
    proposedProperties: ProposedProperty[] | undefined;
    setProjectDetailModal: React.Dispatch<React.SetStateAction<{
        isOpen?: boolean;
        projectId?: string;
    }>>;
    showThirdColumn: { [key: string]: boolean } | undefined;
    setPropertyDetailModal: React.Dispatch<React.SetStateAction<{
        isOpen?: boolean;
        propertyId?: string;
    }>>;
    selectedFloorplanFurnishing: { [key: string]: string } | undefined;
    onSelectHandler: (propertys: PropertyDetails[]) => void;
    onSelectExcludeFloor: ((propertys: PropertyDetails[]) => void) | undefined;
    onSelectExcludePhotoGalleryHandler: ((propertys: PropertyDetails[]) => void) | undefined;
    onSelectExcludeFurnishingHandler: ((propertys: PropertyDetails[]) => void) | undefined;
}

export default function ShortlistedPropertiesListView({
    selectedExcludeFloorDetails,
    selectedExcludePhotoGalleryDetails,
    selectedExcludeFurnishingDetails,
    onUpdateFloorplanFurnishing,
    selectedProperties,
    sortList,
    setShowThirdColumn,
    groupByList,
    proposedProperties,
    setProjectDetailModal,
    showThirdColumn,
    setPropertyDetailModal,
    selectedFloorplanFurnishing,
    onSelectHandler,
    onSelectExcludeFloor,
    onSelectExcludePhotoGalleryHandler,
    onSelectExcludeFurnishingHandler
}: ShortlistedPropertiesListViewProps) {
    const [propDropdown, setPropDropdown] = React.useState<number | null>(null);

    const [dropPos, setDropPos] = React.useState<{
        open: boolean;
        x: string;
        y: string;
        id: string;
        fieldName: string;
    }>();

    const [excludeFloorPlanIds, setExcludeFloorPlanIds] = React.useState<any>(
        selectedExcludeFloorDetails || []
    );
    const [
        excludePhotoGalleryIds,
        setExcludePhotoGalleryIds,
    ] = React.useState<any>(selectedExcludePhotoGalleryDetails || []);
    const [excludeFurnishingIds, setExcludeFurnishingIds] = React.useState<any>(
        selectedExcludeFurnishingDetails || []
    );
    // // Add state for tracking which projects should show third column
    // const [showThirdColumn, setShowThirdColumn] = React.useState<{[key: string]: boolean}>({});

    const handleFloorplanFurnishingChange = (
        propertyId: string,
        value: string
    ) => {
        onUpdateFloorplanFurnishing?.(propertyId, value);
    };


    const ordinalSuffixOf = (i: number) => {
        const j = i % 10;
        const k = i % 100;
        if (j === 1 && k !== 11) {
            return i + "st";
        }
        if (j === 2 && k !== 12) {
            return i + "nd";
        }
        if (j === 3 && k !== 13) {
            return i + "rd";
        }
        return i + "th";
    };

    const dropdownClick = (event: any, id: string, fieldName: string) => {
        const pos = event.target.getBoundingClientRect();
        const x = pos.x + "px";
        const y = pos.y + pos.height + "px";
        setDropPos({ open: !dropPos?.open, x, y, id, fieldName });
    };

    const renderFieldDetails = (fieldName: string, fieldArrayValues: any) => {
        return (
            <div
                className="position-absolute bg-white shadow w-100 pt-2"
                style={{ right: 0, minWidth: "250px", maxWidth: "300px", zIndex: 100 }}
            >
                <div className="px-3 pt-2 font-normal text-gray-600 font-weight-semibold my-1">
                    {fieldName}
                </div>
                {fieldArrayValues?.map((item: any, index: number) => {
                    return (
                        <div
                            className="d-flex align-items-center text-nowrap py-2 px-3 border-bottom overflow-hidden"
                            key={index}
                        >
                            <img
                                src={item?.profileImage ? item?.profileImage : BlankProPic}
                                alt="image_not_found"
                                className="img-cover rounded-circle img-responsive mr-2"
                                width={26}
                                height={26}
                            />
                            <div className="font-weight-semibold w-100">
                                <div style={{ maxWidth: '180px' }}>
                                    <div className="font-normal truncate w-100" title={item.contactName}>
                                        {item.contactName}
                                    </div>
                                    <div className="font-xs text-gray-600 d-flex align-items-center truncate w-100">
                                        {item.phones.map((itm: any, idx: number) => (
                                            <>
                                                <PhoneNumberCopy>{itm.phone}</PhoneNumberCopy>
                                                {++idx < item?.phones?.length ? ", " : ""}
                                            </>
                                        ))}
                                    </div>
                                    <div className="font-xs text-gray-600 d-flex align-items-center truncate w-100">
                                        {item.emails.map((itm: any, idx: number) => (
                                            <>
                                                <EmailLink className="truncate w-100">{itm.email}</EmailLink>
                                                {++idx < item.emails.length ? ", " : ""}
                                            </>
                                        ))}
                                    </div>
                                    <div className="font-xs text-gray-600 d-flex align-items-center truncate w-100">
                                        {item?.type}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const [selectedProperty, setSelectedProperty] = React.useState<
        PropertyDetails[]
    >(selectedProperties);

    const onAllSelected = (selected: boolean, projectId: string) => {
        const allValues = !selected
            ? []
            : sortList
                ?.filter((x) => x.propertyDetailsFromProject?.projectId === projectId)
                ?.map((x) => {
                    return {
                        ...x.propertyDetailsFromProject,
                        latitude: x?.propertyDetails?.proLatitude,
                        longitude: x?.propertyDetails?.proLongitude,
                        cityName: x?.propertyDetails?.cityName,
                        cityLocName: x?.propertyDetails?.cityLocName,
                        propertyDetails: x?.propertyDetails,
                    };
                });

        setSelectedProperty([
            ...(selectedProperty?.filter(
                (p) => p.projectId?.toString() !== projectId?.toString()
            ) || []),
            ...(allValues || []),
        ]);

        if (!selected) {
            setExcludeFloorPlanIds([
                ...(excludeFloorPlanIds?.filter(
                    (p: { projectId: { toString: () => string } }) =>
                        p.projectId?.toString() !== projectId?.toString()
                ) || []),
                ...[],
            ]);
        }
    };

    const onSelectAllValues = (selected: boolean) => {
        const allValues = !selected
            ? []
            : sortList?.map((x) => {
                return {
                    ...x.propertyDetailsFromProject,
                    latitude: x?.propertyDetails?.proLatitude,
                    longitude: x?.propertyDetails?.proLongitude,
                    cityName: x?.propertyDetails?.cityName,
                    cityLocName: x?.propertyDetails?.cityLocName,
                    propertyDetails: x?.propertyDetails,
                };
            });
        setSelectedProperty(allValues || []);
    };

    const onSelect = (sel: boolean, property: PropertyDetails) => {
        if (sel) {
            setSelectedProperty([...(selectedProperty || []), property]);
        } else {
            setSelectedProperty(
                selectedProperty?.filter(
                    (val) => val.propertyId !== property.propertyId
                ) || []
            );

            setExcludeFloorPlanIds(
                excludeFloorPlanIds?.filter(
                    (val: { propertyId: any }) => val.propertyId !== property.propertyId
                ) || []
            );
        }
    };

    const onSelectExcludeFloorPlan = (sel: boolean, property: any) => {
        if (sel) {
            setExcludeFloorPlanIds([...(excludeFloorPlanIds || []), property]);
        } else {
            setExcludeFloorPlanIds(
                excludeFloorPlanIds?.filter(
                    (val: { propertyId: any }) => val.propertyId !== property.propertyId
                ) || []
            );
        }
    };

    const onAllSelectedExcludeFloorPlan = (
        selected: boolean,
        projectId: string
    ) => {
        const allValues = !selected
            ? []
            : sortList
                ?.filter((x) => x.propertyDetailsFromProject?.projectId === projectId)
                ?.map((x) => {
                    return {
                        ...x.propertyDetailsFromProject,
                        propertyDetails: x?.propertyDetails,
                    };
                });

        setExcludeFloorPlanIds([
            ...(excludeFloorPlanIds?.filter(
                (p: { projectId: { toString: () => string } }) =>
                    p.projectId?.toString() !== projectId?.toString()
            ) || []),
            ...(allValues || []),
        ]);
    };

    const onSelectExcludePhotoGallery = (sel: boolean, property: any) => {
        if (sel) {
            setExcludePhotoGalleryIds([...(excludePhotoGalleryIds || []), property]);
        } else {
            setExcludePhotoGalleryIds(
                excludePhotoGalleryIds?.filter(
                    (val: { propertyId: any }) => val.propertyId !== property.propertyId
                ) || []
            );
        }
    };

    const onAllSelectedExcludePhotoGallery = (
        selected: boolean,
        projectId: string
    ) => {
        const allValues = !selected
            ? []
            : sortList
                ?.filter((x) => x.propertyDetailsFromProject?.projectId === projectId)
                ?.map((x) => {
                    return {
                        ...x.propertyDetailsFromProject,
                        propertyDetails: x?.propertyDetails,
                    };
                });

        setExcludePhotoGalleryIds([
            ...(excludePhotoGalleryIds?.filter(
                (p: { projectId: { toString: () => string } }) =>
                    p.projectId?.toString() !== projectId?.toString()
            ) || []),
            ...(allValues || []),
        ]);
    };

    const onSelectExcludeFurnishing = (sel: boolean, property: any) => {
        if (sel) {
            setExcludeFurnishingIds([...(excludeFurnishingIds || []), property]);
        } else {
            setExcludeFurnishingIds(
                excludeFurnishingIds?.filter(
                    (val: { propertyId: any }) => val.propertyId !== property.propertyId
                ) || []
            );
        }
    };

    const onAllSelectedExcludeFurnishing = (
        selected: boolean,
        projectId: string
    ) => {
        const allValues = !selected
            ? []
            : sortList
                ?.filter((x) => x.propertyDetailsFromProject?.projectId === projectId)
                ?.map((x) => {
                    return {
                        ...x.propertyDetailsFromProject,
                        propertyDetails: x?.propertyDetails,
                    };
                });

        setExcludeFurnishingIds([
            ...(excludeFurnishingIds?.filter(
                (p: { projectId: { toString: () => string } }) =>
                    p.projectId?.toString() !== projectId?.toString()
            ) || []),
            ...(allValues || []),
        ]);
    };

    // Add handler for third column checkbox
    const handleThirdColumnChange = (projectId: string, checked: boolean) => {
        setShowThirdColumn?.((prev) => ({ ...prev, [projectId]: checked }));

        // Update all properties in this project to ThreeColumn
        const projectProperties =
            sortList?.filter(
                (x) => x.propertyDetailsFromProject?.projectId === projectId
            ) || [];
        projectProperties.forEach((item) => {
            onUpdateFloorplanFurnishing?.(
                item?.propertyDetailsFromProject?.propertyId || "",
                checked
                    ? FurnishingDetailsViewOption.ThreeColumn
                    : FurnishingDetailsViewOption.TwoColumn
            );
        });
    };

    return (
        <>
            {groupByList && (
                <>
                    <div className="shortlist-table-outer">
                        <div className="table-responsive shortListProperty-Table">
                            <table className="table w-100">
                                <tr className="font-sm text-gray-600">
                                    <th className="py-3 border-bottom">
                                        <div className="py-1">
                                            <input
                                                type="checkbox"
                                                name={`checkprop-create-proposal`}
                                                id={`checkprop-create-proposal`}
                                                onChange={(e) =>
                                                    onSelectAllValues(e.target.checked)
                                                }
                                                checked={
                                                    selectedProperty?.length ===
                                                    sortList?.length
                                                }
                                            />
                                            <label
                                                htmlFor={`checkprop-create-proposal`}
                                            ></label>
                                        </div>
                                    </th>
                                    <th
                                        className="py-3 border-bottom"
                                        style={{ maxWidth: "90px", width: "90px" }}
                                    ></th>
                                    <th
                                        className="py-3 border-bottom font-weight-semibold "
                                        style={{ width: "290px", maxWidth: "290px" }}
                                    >
                                        Property
                                    </th>
                                    <th
                                        className="py-3 border-bottom font-weight-semibold  text-center"
                                        style={{ width: "194px" }}
                                    >
                                        Version
                                    </th>
                                    <th className="py-3 border-bottom font-weight-semibold ">
                                        Square feet area
                                    </th>
                                    <th className="py-3 border-bottom font-weight-semibold ">
                                        Developer
                                    </th>

                                    <th className="py-3 border-bottom font-weight-semibold ">
                                        Added Units
                                    </th>
                                    <th className="py-3 border-bottom font-weight-semibold"></th>
                                </tr>
                                {/* {[...Array(3)].map((item, index) => { */}
                                {Object.keys(groupByList).map((key, index) => {
                                    const item = groupByList[key]?.find(
                                        (x: any) =>
                                            x.propertyDetailsFromProject.projectId === key
                                    );

                                    const totalArea =
                                        groupByList[key]?.reduce(
                                            (t: any, c: any) =>
                                                t +
                                                Number(c.propertyDetailsFromProject?.area || 0),
                                            0
                                        ) || 0;

                                    const unitDetails = groupByList[
                                        key
                                    ]?.sort((a: any, b: any) =>
                                        a.propertyDetailsFromProject?.floor <
                                            b.propertyDetailsFromProject?.floor
                                            ? -1
                                            : 1
                                    );

                                    const floors = [
                                        ...new Set(
                                            unitDetails?.map(
                                                (f: any) => f.propertyDetailsFromProject?.floor
                                            )
                                        ),
                                    ].sort();

                                    const isApproved = !!proposedProperties?.find(
                                        (x) =>
                                            x?.projectDetails?._id ===
                                            item?.propertyDetailsFromProject?.projectId &&
                                            x?.proposalType === ProposalType.Approved
                                    );
                                    const isSent =
                                        !isApproved &&
                                        !!proposedProperties?.find(
                                            (x) =>
                                                x?.projectDetails?._id ===
                                                item?.propertyDetailsFromProject?.projectId &&
                                                x?.proposalType === ProposalType.Sent
                                        );
                                    const isDraft =
                                        !isApproved &&
                                        !isSent &&
                                        !!proposedProperties?.find(
                                            (x) =>
                                                x?.projectDetails?._id ===
                                                item?.propertyDetailsFromProject?.projectId &&
                                                x?.proposalType === ProposalType.Draft
                                        );

                                    const isRejected =
                                        !isApproved &&
                                        !isSent &&
                                        !isDraft &&
                                        !!proposedProperties?.find(
                                            (x) =>
                                                x?.projectDetails?._id ===
                                                item?.propertyDetailsFromProject?.projectId &&
                                                x?.proposalType === ProposalType.Rejected
                                        );

                                    return (
                                        <>
                                            <tr
                                                className={`require-row ${propDropdown === index ? "active" : ""
                                                    }`}
                                            >
                                                <td
                                                    className="py-2 border-bottom"
                                                    style={{ maxWidth: "40px", width: "40px" }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="py-1">
                                                            <input
                                                                type="checkbox"
                                                                name={`properyCheck-1-${item?.propertyDetailsFromProject?.projectId}`}
                                                                id={`properyCheck-1-${item?.propertyDetailsFromProject?.projectId}`}
                                                                onChange={(e) =>
                                                                    onAllSelected(
                                                                        e.target.checked,
                                                                        item?.propertyDetailsFromProject
                                                                            ?.projectId
                                                                    )
                                                                }
                                                                checked={
                                                                    selectedProperty?.filter(
                                                                        (ff) =>
                                                                            ff.projectId ===
                                                                            item?.propertyDetailsFromProject
                                                                                ?.projectId
                                                                    )?.length === unitDetails?.length
                                                                }
                                                            />
                                                            <label
                                                                htmlFor={`properyCheck-1-${item?.propertyDetailsFromProject?.projectId}`}
                                                            ></label>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td
                                                    className="py-2 border-bottom"
                                                    style={{ maxWidth: "90px", width: "90px" }}
                                                    role="button"
                                                    onClick={() =>
                                                        setProjectDetailModal({
                                                            isOpen: true,
                                                            projectId: item?.propertyDetailsFromProject?.projectId,
                                                        })
                                                    }
                                                >
                                                    <div className="py-1 text-center">
                                                        <img
                                                            src={
                                                                item?.propertyDetailsFromProject
                                                                    ?.propertyCoverImage
                                                            }
                                                            alt={
                                                                item?.propertyDetailsFromProject
                                                                    ?.projectName
                                                            }
                                                            width={59}
                                                            height={45}
                                                            className="img-cover"
                                                        />
                                                    </div>
                                                </td>
                                                <td
                                                    className="py-2 border-bottom"
                                                    style={{ width: "290px", maxWidth: "290px" }}
                                                    role="button"
                                                    onClick={() =>
                                                        setProjectDetailModal({
                                                            isOpen: true,
                                                            projectId: item?.propertyDetailsFromProject?.projectId,
                                                        })
                                                    }
                                                >
                                                    <div className="py-1 w-100">
                                                        <div className="font-sm text-truncate mb-1">
                                                            {
                                                                item?.propertyDetailsFromProject
                                                                    ?.projectName
                                                            }
                                                        </div>
                                                        <div className="font-xs text-gray-500 line-clamp-2">
                                                            {
                                                                item?.propertyDetailsFromProject
                                                                    ?.propertyAddress
                                                            }
                                                        </div>
                                                        {isApproved && (
                                                            <span className="tag--approved font-xxs">
                                                                Approved
                                                            </span>
                                                        )}
                                                        {isSent && (
                                                            <span className="tag--active font-xxs">
                                                                Proposed
                                                            </span>
                                                        )}
                                                        {isDraft && (
                                                            <span className="tag--adminreviewrequested font-xxs">
                                                                Requested
                                                            </span>
                                                        )}

                                                        {isRejected && (
                                                            <span className="tag--adminrejected font-xxs">
                                                                Rejected
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td
                                                    className="py-2 border-bottom"
                                                    style={{ width: "194px" }}
                                                >
                                                    <div className="py-1 font-sm text-center">
                                                        V{item?.requirementVersion}
                                                    </div>
                                                </td>
                                                <td className="py-2 border-bottom">
                                                    <div className="py-1 font-xs text-gray-800">
                                                        <b>{totalArea?.toFixed(2)}</b> /Sq. Ft
                                                    </div>
                                                </td>
                                                <td className="py-2 border-bottom">
                                                    <div className="py-1 font-xs text-gray-800 font-weight-semibold">
                                                        {
                                                            item?.propertyDetailsFromProject
                                                                ?.developer
                                                        }
                                                    </div>
                                                </td>
                                                <td className="py-2 border-bottom">
                                                    <div className="py-1">
                                                        <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                                                            Floor:{" "}
                                                            {
                                                                groupByList[key][0]
                                                                    ?.propertyDetailsFromProject?.floor
                                                            }{" "}
                                                            / Unit:{" "}
                                                            {
                                                                groupByList[key][0]
                                                                    ?.propertyDetailsFromProject?.unitName
                                                            }{" "}
                                                            / Area:{" "}
                                                            {
                                                                groupByList[key][0]
                                                                    ?.propertyDetailsFromProject?.area
                                                            }
                                                        </div>
                                                        {groupByList[key]?.length > 1 && (
                                                            <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                                                                +{groupByList[key]?.length - 1}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-2 border-bottom">
                                                    <div
                                                        className="py-1 cursor-pointer d-flex align-items-center"
                                                        onClick={() =>
                                                            propDropdown === index
                                                                ? setPropDropdown(null)
                                                                : setPropDropdown(index)
                                                        }
                                                    >
                                                        <span className="text-nowrap font-sm font-weight-semibold mr-2">
                                                            View Details
                                                        </span>
                                                        <ArrowDownIcon
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                            {propDropdown === index && (
                                                <tr>
                                                    <td colSpan={8}>
                                                        <ShortlistedPropertiesDropdownTable
                                                            dropdownClick={dropdownClick}
                                                            dropPos={dropPos}
                                                            excludeFloorPlanIds={excludeFloorPlanIds}
                                                            excludeFurnishingIds={excludeFurnishingIds}
                                                            excludePhotoGalleryIds={excludePhotoGalleryIds}
                                                            floors={floors}
                                                            handleFloorplanFurnishingChange={handleFloorplanFurnishingChange}
                                                            handleThirdColumnChange={handleThirdColumnChange}
                                                            item={item}
                                                            onAllSelected={onAllSelected}
                                                            onAllSelectedExcludeFloorPlan={onAllSelectedExcludeFloorPlan}
                                                            onAllSelectedExcludeFurnishing={onAllSelectedExcludeFurnishing}
                                                            onAllSelectedExcludePhotoGallery={onAllSelectedExcludePhotoGallery}
                                                            onSelect={onSelect}
                                                            onSelectExcludeFloorPlan={onSelectExcludeFloorPlan}
                                                            onSelectExcludeFurnishing={onSelectExcludeFurnishing}
                                                            onSelectExcludePhotoGallery={onSelectExcludePhotoGallery}
                                                            ordinalSuffixOf={ordinalSuffixOf}
                                                            proposedProperties={proposedProperties}
                                                            renderFieldDetails={renderFieldDetails}
                                                            selectedFloorplanFurnishing={selectedFloorplanFurnishing}
                                                            selectedProperty={selectedProperty}
                                                            setPropertyDetailModal={setPropertyDetailModal}
                                                            showThirdColumn={showThirdColumn}
                                                            unitDetails={unitDetails}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}
                            </table>
                        </div>
                    </div>
                    <div className="d-flex justify-content-end mt-3 ">
                        <div>
                            <span className="pr-3 font-sm">
                                {!!selectedProperty?.length && (
                                    <>
                                        <span className="pr-2 text-gray-600">
                                            <span className="font-weight-bold text-gray-700">
                                                {
                                                    [
                                                        ...new Set(
                                                            selectedProperty?.map((x) => x.propertyId)
                                                        ),
                                                    ]?.length
                                                }{" "}
                                            </span>
                                            Properties
                                        </span>

                                        <span className="text-gray-600">
                                            <span className="font-weight-bold text-gray-700">
                                                {
                                                    [
                                                        ...new Set(
                                                            selectedProperty?.map((x) => x.projectId)
                                                        ),
                                                    ]?.length
                                                }{" "}
                                            </span>
                                            Projects
                                        </span>
                                    </>
                                )}
                            </span>

                            <button
                                type="button"
                                onClick={() => {
                                    onSelectHandler(selectedProperty!);
                                    onSelectExcludeFloor?.(excludeFloorPlanIds!);
                                    onSelectExcludePhotoGalleryHandler?.(
                                        excludePhotoGalleryIds!
                                    );
                                    onSelectExcludeFurnishingHandler?.(
                                        excludeFurnishingIds!
                                    );
                                }}
                                className="btn bg-primary font-normal font-weight-bold text-white select-prop-bt"
                                disabled={!selectedProperty?.length}
                            >
                                Select Properties
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
